import { useState } from 'react';
import {
  Modal,
  Upload,
  Button,
  Table,
  Alert,
  Space,
  Typography,
  Form,
  Input,
  Checkbox,
  App,
} from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { EcnLine } from '@bom/shared';
import { readWorkbook, parseEcn } from '../../lib/excel';
import { useCreateEcn } from '../../api/ecns';
import { useEmailConfig } from '../../api/email-config';
import { useContacts } from '../../api/contacts';
import { usePresignUpload, putFileToS3 } from '../../api/attachments';

const { Dragger } = Upload;

interface Props {
  tid: string;
  open: boolean;
  onClose: () => void;
}

function mimeByName(
  n: string,
):
  | 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  | 'application/vnd.ms-excel' {
  return n.toLowerCase().endsWith('.xlsx')
    ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    : 'application/vnd.ms-excel';
}

export function EcnUploadModal({ tid, open, onClose }: Props) {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [form] = Form.useForm<{
    ecn_date: string;
    affected_pcba?: string;
    change_subject?: string;
  }>();
  const [file, setFile] = useState<File | null>(null);
  const [lines, setLines] = useState<EcnLine[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [notify, setNotify] = useState(true);
  const [notifySubject, setNotifySubject] = useState('');
  const [notifyBody, setNotifyBody] = useState('');
  const presign = usePresignUpload(tid);
  const create = useCreateEcn(tid);
  const emailCfg = useEmailConfig(tid);
  const configured = !!emailCfg.data?.has_password;
  const contactsQ = useContacts(tid);
  const hasContacts = (contactsQ.data ?? []).some((c) => c.is_active !== false);
  const smtpMissing = notify && !configured;
  const contactsMissing = notify && !hasContacts;
  const blocked = smtpMissing || contactsMissing; // 勾了通知但未配 SMTP / 无联系人 → 拦截
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    form.resetFields();
    setFile(null);
    setLines([]);
    setErrors([]);
    setNotify(true);
    setNotifySubject('');
    setNotifyBody('');
  };

  const close = () => {
    reset();
    onClose();
  };

  const goConfig = () => {
    close();
    navigate('/email-config');
  };

  const goContacts = () => {
    close();
    navigate('/members?tab=contacts');
  };

  const handleFile = async (f: File) => {
    try {
      const wb = await readWorkbook(f);
      const r = parseEcn(wb, wb.SheetNames[0]!);
      setFile(f);
      setLines(r.lines);
      setErrors(r.errors.map((e) => e.reason));
      form.setFieldsValue({
        ecn_date: r.ecn_date,
        affected_pcba: r.affected_pcba,
        change_subject: r.change_subject,
      });
    } catch {
      message.error('读取/解析失败（请用 ECN .xls/.xlsx）');
    }
    return false;
  };

  const submit = async () => {
    if (!file || lines.length === 0) {
      message.warning('请先上传并解析出变更明细');
      return;
    }
    if (blocked) {
      message.warning('勾选了发送邮件，但发件邮箱或通知联系人未就绪，请先处理或取消勾选');
      return;
    }
    const v = await form.validateFields();
    setSubmitting(true);
    try {
      const ct = mimeByName(file.name);
      const pre = await presign.mutateAsync({
        filename: file.name,
        content_type: ct,
        size_bytes: file.size,
        purpose: 'general',
      });
      await putFileToS3(pre.upload_url, file, ct);
      const r = await create.mutateAsync({
        ecn_date: v.ecn_date,
        ...(v.affected_pcba ? { affected_pcba: v.affected_pcba } : {}),
        ...(v.change_subject ? { change_subject: v.change_subject } : {}),
        source_file_name: file.name,
        source_file_s3_key: pre.s3_key,
        lines,
        notify,
        ...(notify && notifySubject.trim() ? { notify_subject: notifySubject.trim() } : {}),
        ...(notify && notifyBody.trim() ? { notify_body: notifyBody.trim() } : {}),
      });
      if (notify && r.notify_error)
        message.warning(`ECN 已上传，但通知发送失败：${r.notify_error}（可在列表点「重发」）`);
      else message.success(r.notified ? 'ECN 已上传并发送通知' : 'ECN 已上传');
      close();
    } catch (e) {
      message.error((e as Error).message || '上传失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title="上传 ECN 变更单"
      open={open}
      width={920}
      onCancel={close}
      footer={[
        <Button key="c" onClick={close}>
          取消
        </Button>,
        <Button
          key="ok"
          type="primary"
          loading={submitting}
          disabled={!file || lines.length === 0 || blocked}
          onClick={submit}
        >
          上传（{lines.length} 项变更）
        </Button>,
      ]}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {!file ? (
          <Dragger
            accept=".xls,.xlsx"
            beforeUpload={handleFile}
            showUploadList={false}
            maxCount={1}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">点击或拖拽 ECN .xls / .xlsx 到此处</p>
            <p className="ant-upload-hint">
              自动解析「日期 / 变更料号 / 变更前后明细」，原文件留存可下载
            </p>
          </Dragger>
        ) : (
          <>
            <Space>
              <Typography.Text type="secondary">已选：{file.name}</Typography.Text>
              <Button size="small" onClick={reset}>
                重新选择
              </Button>
            </Space>
            {errors.length > 0 && <Alert type="warning" showIcon message={errors.join('；')} />}
            <Form form={form} layout="inline">
              <Form.Item
                name="ecn_date"
                label="日期"
                rules={[{ required: true, message: 'YYYYMMDD' }]}
              >
                <Input placeholder="YYYYMMDD" style={{ width: 130 }} />
              </Form.Item>
              <Form.Item name="affected_pcba" label="变更料号">
                <Input style={{ width: 180 }} />
              </Form.Item>
            </Form>
            <Checkbox checked={notify} onChange={(e) => setNotify(e.target.checked)}>
              发布后通知联系人（附原文件）
            </Checkbox>
            {smtpMissing && (
              <Alert
                type="warning"
                showIcon
                message="尚未配置发件邮箱"
                description="勾选“发送邮件”需先配置本产品的 SMTP 发件邮箱；或取消勾选后直接上传。"
                action={
                  <Button size="small" type="primary" onClick={goConfig}>
                    去配置
                  </Button>
                }
              />
            )}
            {contactsMissing && (
              <Alert
                type="warning"
                showIcon
                message="尚未配置通知联系人"
                description="勾选“发送邮件”需先添加本产品的通知联系人；或取消勾选后直接上传。"
                action={
                  <Button size="small" type="primary" onClick={goContacts}>
                    去添加联系人
                  </Button>
                }
              />
            )}
            {notify && !blocked && (
              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                <Input
                  placeholder="邮件主题（留空用默认：项目名称-BOM/ECN 主要修改内容）"
                  value={notifySubject}
                  maxLength={200}
                  onChange={(e) => setNotifySubject(e.target.value)}
                />
                <Input.TextArea
                  placeholder="邮件正文（留空用默认）"
                  value={notifyBody}
                  rows={3}
                  maxLength={5000}
                  onChange={(e) => setNotifyBody(e.target.value)}
                />
              </Space>
            )}
            <Table<EcnLine>
              rowKey="no"
              size="small"
              dataSource={lines}
              pagination={false}
              scroll={{ y: 360 }}
              columns={[
                { title: 'No', dataIndex: 'no', width: 50 },
                { title: '变更前料号', render: (_, r) => r.before.part_no || '—', width: 130 },
                { title: '变更前规格', render: (_, r) => r.before.spec || '—', ellipsis: true },
                { title: '变更后料号', render: (_, r) => r.after.part_no || '—', width: 130 },
                { title: '变更后规格', render: (_, r) => r.after.spec || '—', ellipsis: true },
                {
                  title: '备注',
                  dataIndex: 'remark',
                  width: 100,
                  render: (v?: string) => v ?? '—',
                },
              ]}
            />
          </>
        )}
      </Space>
    </Modal>
  );
}
